<template>
  <div class="min-h-screen bg-gray-50">

    <!-- Header -->
    <div class="bg-white shadow">
      <div class="w-full px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <i class="fas fa-broom text-amber-500 text-3xl"></i>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Simulation nettoyage des visites</h1>
              <p class="text-sm text-gray-500 mt-1">
                Estime le nombre de visites et contrôles supprimés si l'on ne garde que
                <strong>N visites par lot</strong> (les plus récentes)
              </p>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <!-- Paramètre keepPerLot -->
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-gray-700 whitespace-nowrap">
                Visites à garder par lot :
              </label>
              <input
                v-model.number="keepPerLot"
                type="number"
                min="1"
                max="99"
                class="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm text-center focus:outline-none focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <!-- Recherche -->
            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="fas fa-search text-gray-400 text-xs"></i>
              </span>
              <input
                v-model="search"
                type="text"
                placeholder="Filtrer par NSIL ou adresse…"
                class="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500 w-52"
              />
              <button
                v-if="search"
                @click="search = ''"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
            <!-- Bouton simuler -->
            <button
              @click="simulate"
              :disabled="loading"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-calculator'" class="mr-2"></i>
              {{ loading ? 'Calcul…' : 'Simuler' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="w-full px-4 sm:px-6 lg:px-8 py-8">

      <!-- Erreur -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
        <div class="flex">
          <i class="fas fa-exclamation-circle text-red-400 text-xl flex-shrink-0 mt-0.5"></i>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Erreur lors du calcul</h3>
            <p class="mt-1 text-sm text-red-700">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- État initial -->
      <div v-else-if="!simulated && !loading" class="text-center py-20">
        <i class="fas fa-broom text-gray-300 text-6xl mb-4"></i>
        <p class="text-gray-500 text-lg mb-2">Aucune simulation effectuée</p>
        <p class="text-gray-400 text-sm mb-6">
          Configurez le nombre de visites à garder par lot, puis cliquez sur "Simuler".
        </p>
        <button
          @click="simulate"
          class="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none"
        >
          <i class="fas fa-calculator mr-2"></i>
          Lancer la simulation
        </button>
      </div>

      <!-- Chargement -->
      <div v-else-if="loading" class="text-center py-20">
        <i class="fas fa-spinner fa-spin text-amber-500 text-5xl mb-4"></i>
        <p class="text-gray-600">Simulation en cours…</p>
        <p class="text-gray-400 text-sm mt-2">Calcul des visites et contrôles impactés</p>
      </div>

      <!-- Résultats -->
      <template v-else-if="simulated">

        <!-- Bandeau synthèse -->
        <div
          class="mb-6 rounded-xl overflow-hidden shadow-sm border"
          :class="result.summary.totalVisitsToDelete > 0 ? 'border-amber-200' : 'border-green-200'"
        >
          <!-- Titre synthèse -->
          <div
            class="px-5 py-3 text-sm font-semibold flex items-center gap-2"
            :class="result.summary.totalVisitsToDelete > 0 ? 'bg-amber-600 text-white' : 'bg-green-600 text-white'"
          >
            <i class="fas fa-chart-bar"></i>
            Synthèse — simulation avec {{ result.keepPerLot }} visite(s) max par lot
          </div>

          <!-- Cartes de stats -->
          <div class="bg-white grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-x divide-y divide-gray-100">

            <!-- Lots impactés -->
            <div class="px-5 py-4 text-center">
              <div class="text-3xl font-extrabold text-amber-600">
                {{ result.summary.totalPropertiesAffected.toLocaleString('fr-FR') }}
              </div>
              <div class="text-xs text-gray-500 mt-1">lots impactés</div>
            </div>

            <!-- Visites à supprimer -->
            <div class="px-5 py-4 text-center">
              <div class="text-3xl font-extrabold text-red-600">
                {{ result.summary.totalVisitsToDelete.toLocaleString('fr-FR') }}
              </div>
              <div class="text-xs text-gray-500 mt-1">visites à supprimer</div>
            </div>

            <!-- Contrôles à supprimer -->
            <div class="px-5 py-4 text-center">
              <div class="text-3xl font-extrabold text-red-700">
                {{ result.summary.totalControlsToDelete.toLocaleString('fr-FR') }}
              </div>
              <div class="text-xs text-gray-500 mt-1">contrôles à supprimer</div>
            </div>

            <!-- Total visites actuelles -->
            <div class="px-5 py-4 text-center">
              <div class="text-3xl font-extrabold text-gray-600">
                {{ result.summary.totalVisitsTotal.toLocaleString('fr-FR') }}
              </div>
              <div class="text-xs text-gray-500 mt-1">visites totales (lots impactés)</div>
            </div>

            <!-- Total contrôles actuels -->
            <div class="px-5 py-4 text-center">
              <div class="text-3xl font-extrabold text-gray-600">
                {{ result.summary.totalControlsTotal.toLocaleString('fr-FR') }}
              </div>
              <div class="text-xs text-gray-500 mt-1">contrôles totaux (lots impactés)</div>
            </div>

          </div>

          <!-- Barre de progression de réduction -->
          <div v-if="result.summary.totalVisitsTotal > 0" class="bg-white px-5 py-3 border-t border-gray-100">
            <div class="flex items-center gap-3">
              <span class="text-xs text-gray-500 w-32 flex-shrink-0">Réduction des visites</span>
              <div class="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  class="h-2.5 rounded-full bg-red-500 transition-all duration-500"
                  :style="{ width: reductionPercentVisits + '%' }"
                ></div>
              </div>
              <span class="text-xs font-bold text-red-600 w-12 text-right">{{ reductionPercentVisits }}%</span>
            </div>
            <div class="flex items-center gap-3 mt-1">
              <span class="text-xs text-gray-500 w-32 flex-shrink-0">Réduction des contrôles</span>
              <div class="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  class="h-2.5 rounded-full bg-red-700 transition-all duration-500"
                  :style="{ width: reductionPercentControls + '%' }"
                ></div>
              </div>
              <span class="text-xs font-bold text-red-700 w-12 text-right">{{ reductionPercentControls }}%</span>
            </div>
          </div>
        </div>

        <!-- Aucun lot impacté -->
        <div
          v-if="result.summary.totalPropertiesAffected === 0"
          class="text-center py-12 bg-white rounded-lg shadow"
        >
          <i class="fas fa-check-circle text-green-400 text-5xl mb-3"></i>
          <p class="text-gray-700 font-medium">Aucun lot n'a plus de {{ result.keepPerLot }} visites.</p>
          <p class="text-gray-400 text-sm mt-1">Aucune suppression ne serait nécessaire.</p>
        </div>

        <!-- Tableau détaillé -->
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">

          <!-- Barre de résultats -->
          <div class="px-5 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
            <span class="text-sm text-gray-500">
              <span class="font-semibold text-gray-700">{{ filteredDetails.length }}</span>
              / {{ result.details.length }} lot(s) affiché(s)
            </span>
            <span class="text-xs text-gray-400 italic">
              Trié par nombre de visites à supprimer (décroissant)
            </span>
          </div>

          <!-- Tableau -->
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 text-sm">
              <thead class="bg-gray-800 text-white">
                <tr>
                  <th class="px-4 py-3 text-left font-semibold w-8">#</th>
                  <th class="px-4 py-3 text-left font-semibold whitespace-nowrap">Lot (NSIL)</th>
                  <th class="px-4 py-3 text-left font-semibold whitespace-nowrap">Adresse</th>
                  <th class="px-4 py-3 text-center font-semibold whitespace-nowrap">Total visites</th>
                  <th class="px-4 py-3 text-center font-semibold whitespace-nowrap">À conserver</th>
                  <th class="px-4 py-3 text-center font-semibold whitespace-nowrap">
                    <span class="text-red-300">À supprimer</span>
                  </th>
                  <th class="px-4 py-3 text-center font-semibold whitespace-nowrap">Total contrôles</th>
                  <th class="px-4 py-3 text-center font-semibold whitespace-nowrap">
                    <span class="text-red-300">Contrôles supprimés</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr
                  v-for="(detail, index) in filteredDetails"
                  :key="detail.propertyId"
                  class="hover:bg-amber-50 transition-colors"
                  :class="detail.visitsToDelete >= 5 ? 'bg-red-50' : ''"
                >
                  <!-- # -->
                  <td class="px-4 py-3 text-gray-400 text-xs">{{ index + 1 }}</td>

                  <!-- Lot -->
                  <td class="px-4 py-3">
                    <span class="font-bold text-gray-800">{{ detail.nsil || '—' }}</span>
                    <div class="text-xs text-gray-400 font-mono mt-0.5 select-all" :title="detail.propertyId">
                      {{ detail.propertyId.substring(0, 8) }}…
                    </div>
                  </td>

                  <!-- Adresse -->
                  <td class="px-4 py-3 text-xs text-gray-600 max-w-xs">
                    {{ detail.address || '—' }}
                  </td>

                  <!-- Total visites -->
                  <td class="px-4 py-3 text-center">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-700">
                      {{ detail.totalVisits }}
                    </span>
                  </td>

                  <!-- À conserver -->
                  <td class="px-4 py-3 text-center">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                      {{ detail.visitsToKeep }}
                    </span>
                  </td>

                  <!-- À supprimer (visites) -->
                  <td class="px-4 py-3 text-center">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold"
                      :class="detail.visitsToDelete >= 5 ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'"
                    >
                      − {{ detail.visitsToDelete }}
                    </span>
                  </td>

                  <!-- Total contrôles -->
                  <td class="px-4 py-3 text-center">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-700">
                      {{ detail.controlsTotal }}
                    </span>
                  </td>

                  <!-- Contrôles supprimés -->
                  <td class="px-4 py-3 text-center">
                    <span
                      v-if="detail.controlsToDelete > 0"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold"
                      :class="detail.controlsToDelete >= 20 ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'"
                    >
                      − {{ detail.controlsToDelete }}
                    </span>
                    <span v-else class="text-gray-400 text-xs">0</span>
                  </td>
                </tr>

                <!-- Ligne vide -->
                <tr v-if="filteredDetails.length === 0">
                  <td colspan="8" class="text-center py-10 text-gray-400">
                    Aucun lot ne correspond au filtre.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

      </template>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'AdminVisitsCleanup',

  data() {
    return {
      loading: false,
      simulated: false,
      error: null,
      keepPerLot: 4,
      search: '',

      result: {
        keepPerLot: 4,
        summary: {
          totalPropertiesAffected: 0,
          totalVisitsToDelete: 0,
          totalControlsToDelete: 0,
          totalVisitsTotal: 0,
          totalControlsTotal: 0,
        },
        details: [],
      },
    }
  },

  computed: {
    filteredDetails() {
      if (!this.search.trim()) return this.result.details
      const q = this.search.trim().toLowerCase()
      return this.result.details.filter(d =>
        (d.nsil || '').toLowerCase().includes(q) ||
        (d.address || '').toLowerCase().includes(q)
      )
    },

    reductionPercentVisits() {
      const { totalVisitsTotal, totalVisitsToDelete } = this.result.summary
      if (!totalVisitsTotal) return 0
      return Math.round((totalVisitsToDelete / totalVisitsTotal) * 100)
    },

    reductionPercentControls() {
      const { totalControlsTotal, totalControlsToDelete } = this.result.summary
      if (!totalControlsTotal) return 0
      return Math.round((totalControlsToDelete / totalControlsTotal) * 100)
    },
  },

  mounted() {
    this.simulate()
  },

  methods: {
    async simulate() {
      this.loading = true
      this.error = null

      try {
        const response = await api.getVisitsCleanupSimulation(this.keepPerLot)

        if (!response.success) {
          throw new Error(response.message || 'Erreur inconnue')
        }

        this.result = {
          keepPerLot: response.keepPerLot ?? this.keepPerLot,
          summary: response.summary ?? {
            totalPropertiesAffected: 0,
            totalVisitsToDelete: 0,
            totalControlsToDelete: 0,
            totalVisitsTotal: 0,
            totalControlsTotal: 0,
          },
          details: response.details ?? [],
        }

        this.simulated = true
      } catch (err) {
        this.error = err.message || 'Impossible de lancer la simulation'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
